<template>
  <vs-form ref="inventoryEditForm">
    <JobInventoryMultiGroupsPicker
      ref="jobInventoryMultiGroupsPickerRef"
      data-test="job-inventory-multi-groups-picker"
      :model-value="jobFormDataStore.requestedStops"
      :resource-type-activity-id="resourceTypeActivityId ?? -1"
      :is-disabled="isDisabled"
      :is-location-required="false"
      @update:modelValue="setInventoryGroups($event)"
    />
  </vs-form>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useJobFormDataStore } from '../jobs/jobFormDataStore'
import JobInventoryMultiGroupsPicker from '../tickets/StopsAndInventories/InventoryPickers/JobInventoryMultiGroupsPicker.vue'
import { InventoryPickerGroup } from './StopsAndInventories/InventoryPickers'
import { useInventoryTypesWithResourceTypeActivity } from '@/composables/inventory-types/useInventoryTypes'
import { JobStopType } from '@ironsight/types'
import { useStore } from 'vuex-composition-helpers'
import Activity from '@/models/activity'
import VsForm from '../vision/VsForm.vue'

type ResourceTypeActivity = Activity

const props = defineProps<{
  resourceTypeActivityId?: number | null
  isDisabled?: boolean
}>()
const jobInventoryMultiGroupsPickerRef = ref<InstanceType<
  typeof JobInventoryMultiGroupsPicker
> | null>(null)
const inventoryEditForm = ref()
const jobFormDataStore = useJobFormDataStore()

const store = useStore()
const getActivityById = computed<(id?: number | null) => ResourceTypeActivity>(
  () => store.getters['activities/getById'],
)

const resourceTypeActivityIdRef = computed(() => props.resourceTypeActivityId ?? null)
const { filteredInventoryTypesByResourceTypeActivityId } =
  useInventoryTypesWithResourceTypeActivity(resourceTypeActivityIdRef)
const setInventoryGroups = (groups: InventoryPickerGroup[]) => {
  jobFormDataStore.setRequestedStops(groups)
}

function clearAnyIncompatibleInventories() {
  const newInventories = jobFormDataStore.requestedStops.map((group) => {
    const newItems = group.inventory.map((item) => {
      if (
        !filteredInventoryTypesByResourceTypeActivityId.value
          .flatMap((x) => x.inventorySubTypes)
          .some((it) => it.id === item.inventorySubTypeId)
      ) {
        return { ...item, inventorySubTypeId: null }
      }
      return item
    })
    return { ...group, inventory: newItems }
  })
  jobFormDataStore.setRequestedStops(newInventories)
}

watch(resourceTypeActivityIdRef, function () {
  clearAnyIncompatibleInventories()
  const hasAvailableInventoryButNoneSelected =
    jobFormDataStore.requestedStops.length === 1 &&
    filteredInventoryTypesByResourceTypeActivityId.value.length > 0

  const activity = getActivityById.value(props.resourceTypeActivityId)
  const inventoryAssociations = activity?.inventoryAssociations || []

  if (hasAvailableInventoryButNoneSelected && inventoryAssociations.length > 0) {
    jobInventoryMultiGroupsPickerRef.value?.addGroup(0, JobStopType.DropOff)
  }
})

function validate() {
  return inventoryEditForm.value.isValid()
}

defineExpose({ validate })
</script>
